import { Action, createReducer, on } from "@ngrx/store";

import {
    changeDisplayMode,
    clearFilters,
    clearPriceFilter,
    closeDialog,
    fetchCategoryMetas,
    fetchCategoryMetasFailure,
    fetchCategoryMetasSuccess,
    fetchGridDatasFailure,
    fetchGridDatasSuccess,
    initGridCategory,
    initGridPage,
    loadMoreProducts,
    loadMoreProductsSuccess,
    openDialog,
    clearMenuLevel,
    updateGridFilter,
    updateGridFilterPrice,
    updateGridSort,
    changePreviousPage,
    reinitializePagesLoaded,
    setIsFromPreviousGrid,
    updateMissingFacet,
    clearSortBy,
    setBackToGridProperties,
} from "../actions/grid.actions";
import { MAX_EDITO_NUMBER_PER_GRID } from "../constants/grid.constant";
import {
    checkMissingFacet,
    cleanUrlParameter,
    getParameterFromFhParameter,
    getPriceRangeFromUrl,
    getSelectedFilters,
    getUrlFormattedPrices,
    hasFacetFilterInUrlParameters,
    updateFhParameters,
    updateFiltersCrawlability,
} from "../helpers/criterias-helper/criterias.helper";
import {
    FH_RANGE_PRICE_PARAM,
    DisplayMode,
    GridAlternatives,
    GridCategory,
    GridEdito,
    GridFilterGroup,
    GridMenuLevel,
    GridProduct,
    PriceRange,
    Sort,
    GridCategoryMetas,
    PageType,
    GridType,
    SelectedFilter,
    Facet,
} from "../model/grid.model";

import { checkGridTypeFacet, computeGridType } from "./facet.helper";

export const gridFeatureKey = "grid";
const DIRECT = "direct";

export interface GridState {
    alternatives?: GridAlternatives;
    category?: GridCategory;
    categoryMetas?: GridCategoryMetas;
    dialogOpen: boolean;
    displayMode: DisplayMode;
    editos: GridEdito[] | undefined;
    facet: Facet;
    filterGroups: GridFilterGroup[];
    gridType: GridType;
    loadMore: {
        pending: boolean;
        displayed: boolean;
        activated: boolean;
    };
    menuLevel: GridMenuLevel;
    page: {
        pageNumber: number;
        pagesLoaded: number;
        previousPage: PageType;
        previousCategoryPimCode: string;
    };
    pendingCategoryMetas: boolean;
    pendingGridDatas: boolean;
    backToGridProperties: {
        positionInPage: number;
        productSku?: string;
    };
    prices?: PriceRange;
    products: GridProduct[] | undefined;
    searchTerm?: string;
    selectedProductSku?: string;
    sortBy: Sort;
    total?: number;
    url?: string;
    urlParams: string;
    isFromPreviousGrid: boolean;
    isHeroProduct: boolean;
}

export const initialState: GridState = {
    alternatives: undefined,
    category: undefined,
    categoryMetas: undefined,
    dialogOpen: false,
    displayMode: "grid",
    editos: undefined,
    facet: {
        facetName: "",
        facetNameValue: "",
        combinedFacets: [],
        facetDisplayName: "",
        facetTitle: "",
        altPaths: {},
        enabled: false,
        updateMetas: false,
        missingFacet: false,
        path: "",
    },
    filterGroups: [],
    gridType: GridType.category,
    loadMore: {
        pending: false,
        displayed: false,
        activated: false,
    },
    menuLevel: { menuLv1: DIRECT },
    page: {
        pageNumber: 1,
        pagesLoaded: 1,
        previousPage: "grid-page",
        previousCategoryPimCode: "",
    },
    pendingCategoryMetas: false,
    pendingGridDatas: false,

    backToGridProperties: {
        positionInPage: 0,
        productSku: undefined,
    },
    prices: undefined,
    products: undefined,
    searchTerm: undefined,
    selectedProductSku: undefined,
    sortBy: Sort.relevance,
    total: undefined,
    url: undefined,
    urlParams: "",
    isFromPreviousGrid: false,
    isHeroProduct: false,
};

export const reducer = (
    state: GridState | undefined,
    action: Action,
): GridState => gridReducer(state, action);

const fetchGridDatasSuccessReducer = (
    state: GridState,
    action: ReturnType<typeof fetchGridDatasSuccess>,
): GridState => {
    const editos =
        action.editos?.length > 0
            ? action.editos.slice(0, MAX_EDITO_NUMBER_PER_GRID)
            : [];
    const filterGroups = action.filterGroups ?? [];

    const filterGroupsWithCrawlableCheck = updateFiltersCrawlability(
        filterGroups,
        state.facet.combinedFacets,
        state.urlParams,
    );

    const selectedFilters: SelectedFilter[] = getSelectedFilters(
        filterGroupsWithCrawlableCheck,
    );

    const { isMissingFacet, filteredValues } = checkMissingFacet(
        selectedFilters,
        state.facet.combinedFacets,
    );

    return {
        ...state,
        total: action.total,
        url: action.url,
        alternatives: {
            ...action.alternatives,
            total: action.total,
        },
        filterGroups: filterGroupsWithCrawlableCheck,
        products: action.products,
        editos,
        pendingGridDatas: false,
        loadMore: {
            ...state.loadMore,
            // load more is enabled if there is more product to download
            displayed:
                action.products !== undefined &&
                action.products?.length < action.total,
        },
        facet: {
            ...state.facet,
            combinedFacets: filteredValues,
            altPaths: action.facet?.altPaths || state.facet?.altPaths,
            facetTitle: action.facet?.title ?? "",
            facetDisplayName: action.facet?.name ?? "",
            missingFacet:
                state.facet.missingFacet === true
                    ? state.facet.missingFacet
                    : isMissingFacet,
            path: action.facet?.path || state.facet.path,
        },
        page: {
            ...state.page,
            pageNumber: action.isFirstPage ? 1 : state.page.pageNumber,
        },
        isFromPreviousGrid: action.isFromPreviousGrid ?? false,
        isHeroProduct: action.isHeroProduct,
    };
};

const fetchGridDatasFailureReducer = (
    state: GridState,
    _action: ReturnType<typeof fetchGridDatasFailure>,
): GridState => ({
    ...state,
    pendingGridDatas: false,
});

const loadMoreProductsReducer = (
    state: GridState,
    _action: ReturnType<typeof loadMoreProducts>,
): GridState => ({
    ...state,
    loadMore: {
        ...state.loadMore,
        pending: true,
        // when load more button is clicked, it still activated
        activated: true,
    },
});

const loadMoreProductsSuccessReducer = (
    state: GridState,
    action: ReturnType<typeof loadMoreProductsSuccess>,
): GridState => {
    let new_products = state.products ?? [];
    new_products = [...new_products, ...action.products];

    return {
        ...state,
        total: action.total,
        products: new_products,
        loadMore: {
            ...state.loadMore,
            pending: false,
            // load more is enabled if there is more product to download
            displayed: new_products.length < action.total,
        },
        page: {
            ...state.page,
            pagesLoaded: Number(state.page.pagesLoaded) + 1,
        },
    };
};

const changeDisplayModeReducer = (
    state: GridState,
    action: ReturnType<typeof changeDisplayMode>,
): GridState => {
    if (!action.displayMode) {
        return state;
    }
    return { ...state, displayMode: action.displayMode };
};

const initGridPageReducer = (
    state: GridState,
    action: ReturnType<typeof initGridPage>,
): GridState => {
    const displayMode = action.displayMode || state.displayMode;
    const urlParameters: string = action.fhParams ?? "";
    let sortBy;
    switch (action.sort) {
        case Sort.priceasc: {
            sortBy = Sort.priceasc;
            break;
        }
        case Sort.pricedsc: {
            sortBy = Sort.pricedsc;
            break;
        }
        default: {
            sortBy = Sort.relevance;
        }
    }
    // retrieve price range from url
    const parameter = getParameterFromFhParameter(
        FH_RANGE_PRICE_PARAM,
        urlParameters,
    );
    const prices = getPriceRangeFromUrl(parameter ?? "");

    return {
        ...state,
        displayMode,
        total: undefined,
        searchTerm: action.searchTerm,
        gridType: action.gridType,
        filterGroups: [],
        loadMore: {
            pending: false,
            displayed: false,
            activated: false,
        },
        page: {
            ...state.page,
            pageNumber: action.pageNumber,
            pagesLoaded: state.page.pagesLoaded
                ? Math.min(state.page.pagesLoaded, 3)
                : 1,
        },
        urlParams: urlParameters,
        sortBy,
        prices,
        pendingGridDatas: true,
        facet: {
            ...state.facet,
            ...action.facet,
        },
        products: [],
        editos: [],
    };
};

const initGridCategoryReducer = (
    state: GridState,
    action: ReturnType<typeof initGridCategory>,
): GridState => ({
    ...state,
    facet: initialState.facet,
    category: {
        ...state.category,
        ...action.category,
    },
    menuLevel: action.menuLevel,
    gridType: GridType.category,
});

const fetchCategoryMetasReducer = (
    state: GridState,
    _action: ReturnType<typeof fetchCategoryMetas>,
): GridState => ({
    ...state,
    pendingCategoryMetas: true,
});

const fetchCategoryMetasSuccessReducer = (
    state: GridState,
    action: ReturnType<typeof fetchCategoryMetasSuccess>,
): GridState => ({
    ...state,
    pendingCategoryMetas: false,
    categoryMetas: action.categoryMetas,
});

const fetchCategoryMetasFailureReducer = (
    state: GridState,
    _action: ReturnType<typeof fetchCategoryMetasFailure>,
): GridState => ({
    ...state,
    pendingCategoryMetas: false,
    categoryMetas: {} as GridCategoryMetas,
});

const updateMissingFacetReducer = (
    state: GridState,
    action: ReturnType<typeof updateMissingFacet>,
): GridState => ({
    ...state,
    facet: {
        ...state.facet,
        missingFacet: action.missingFacet,
    },
});

const updateGridFilterReducer = (
    state: GridState,
    action: ReturnType<typeof updateGridFilter>,
): GridState => {
    // By default, delete facetName and facetNameValue
    let facetName = "";
    let facetNameValue = "";

    const isFacetEnabled =
        action.filter.selected && action.filter.facetIsCrawlable;

    if (isFacetEnabled && action.filterGroup.facetUrlParameterName) {
        // retrieve facet name from filterGroup. Eg: facet_category
        facetName = action.filterGroup.facetUrlParameterName;
        // And define facet value to permit in merchandising service to get this facet value
        facetNameValue = action.filter.value;
    }

    const isGridTypeFacet = checkGridTypeFacet(
        state.facet.combinedFacets,
        action.filterGroup.facetUrlParameterName,
    );

    const gridType = computeGridType(
        state.gridType,
        isFacetEnabled,
        isGridTypeFacet,
    );

    let facetChecked: { facetName: string; facetNameValue: string } | undefined;

    if (isFacetEnabled && facetName && facetNameValue) {
        facetChecked = {
            facetName,
            facetNameValue,
        };
    }

    return {
        ...state,
        loadMore: {
            ...state.loadMore,
        },
        urlParams: getUrlParameters(state.prices, action.filter.urlParams),
        facet: {
            ...state.facet,
            facetName,
            facetNameValue,
            combinedFacets: [
                ...state.facet.combinedFacets.filter(
                    (facet) =>
                        isFacetEnabled &&
                        facet.facetName !==
                            action.filterGroup.facetUrlParameterName,
                ),
                ...(facetChecked ? [facetChecked] : []),
            ],
            enabled: isFacetEnabled,
            updateMetas: isFacetEnabled !== state.facet.enabled,
            missingFacet: false,
        },
        filterGroups: state.filterGroups,
        gridType,
        page: { ...state.page, pageNumber: 1, pagesLoaded: 1 },
        pendingGridDatas: true,
    };
};

const getUrlParameters = (
    prices: PriceRange | undefined,
    urlParameters?: string,
): string => {
    if (
        urlParameters !== undefined &&
        hasFacetFilterInUrlParameters(urlParameters)
    ) {
        return prices === undefined
            ? urlParameters
            : updateFhParameters(
                  FH_RANGE_PRICE_PARAM,
                  getUrlFormattedPrices(prices),
                  urlParameters,
              );
    }

    return "";
};

const updateGridFilterPriceReducer = (
    state: GridState,
    action: ReturnType<typeof updateGridFilterPrice>,
): GridState => {
    // save price selection into fh params
    const urlParameters: string = updateFhParameters(
        FH_RANGE_PRICE_PARAM,
        getUrlFormattedPrices(action.prices),
        state.urlParams,
    );
    return {
        ...state,
        loadMore: {
            ...state.loadMore,
        },
        urlParams: urlParameters,
        page: { ...state.page, pageNumber: 1, pagesLoaded: 1 },
        prices: action.prices,
        pendingGridDatas: true,
    };
};

const updateGridSortReducer = (
    state: GridState,
    action: ReturnType<typeof updateGridSort>,
): GridState => ({
    ...state,
    sortBy: action.sortBy.sort,
    loadMore: {
        ...state.loadMore,
    },
    page: { ...state.page, pageNumber: 1, pagesLoaded: 1 },
    pendingGridDatas: true,
});

const clearFiltersReducer = (
    state: GridState,
    _action: ReturnType<typeof clearFilters>,
): GridState => ({
    ...state,
    loadMore: {
        ...state.loadMore,
    },
    prices: undefined,
    filterGroups: [],
    urlParams: "",
    facet: {
        ...state.facet,
        facetName: "",
        facetNameValue: "",
        altPaths: undefined,
        combinedFacets: [],
        enabled: false,
        // If there was a facet, we should update metas
        updateMetas: state.facet.enabled,
        path: "",
    },
    page: { ...state.page, pageNumber: 1, pagesLoaded: 1 },
    pendingGridDatas: true,
});

const clearFilterPriceReducer = (
    state: GridState,
    _action: ReturnType<typeof clearPriceFilter>,
): GridState => ({
    ...state,
    prices: undefined,
    // Remove fh_range_price parameter from urlParams
    urlParams: cleanUrlParameter(state.urlParams, FH_RANGE_PRICE_PARAM),
    pendingGridDatas: true,
});

const clearSortByReducer = (
    state: GridState,
    _action: ReturnType<typeof clearSortBy>,
): GridState => ({
    ...state,
    sortBy: initialState.sortBy,
});

const openDialogReducer = (
    state: GridState,
    _action: ReturnType<typeof openDialog>,
): GridState => ({
    ...state,
    dialogOpen: true,
});

const closeDialogReducer = (
    state: GridState,
    _action: ReturnType<typeof closeDialog>,
): GridState => ({
    ...state,
    dialogOpen: false,
});

const clearMenuLevelReducer = (
    state: GridState,
    _action: ReturnType<typeof clearMenuLevel>,
): GridState => ({
    ...state,
    menuLevel: { menuLv1: DIRECT },
});

const setBackToGridPropertiesReducer = (
    state: GridState,
    action: ReturnType<typeof setBackToGridProperties>,
): GridState => ({
    ...state,
    backToGridProperties: {
        positionInPage: action.position,
        productSku: action.sku,
    },
});

const setIsFromPreviousGridReducer = (
    state: GridState,
    action: ReturnType<typeof setIsFromPreviousGrid>,
): GridState => ({
    ...state,
    isFromPreviousGrid: action.isFromPreviousGrid,
});

const changePreviousPageReducer = (
    state: GridState,
    action: ReturnType<typeof changePreviousPage>,
): GridState => ({
    ...state,
    page: {
        ...state.page,
        previousPage: action.previousPage,
        previousCategoryPimCode: state.category?.pimCode ?? "",
    },
});

const reinitializePagesLoadedReducer = (
    state: GridState,
    _action: ReturnType<typeof reinitializePagesLoaded>,
): GridState => ({
    ...state,
    page: {
        ...state.page,
        pagesLoaded: 1,
        previousCategoryPimCode: state.category?.pimCode ?? "",
    },
});

const gridReducer = createReducer(
    initialState,
    on(initGridPage, initGridPageReducer),
    on(initGridCategory, initGridCategoryReducer),
    on(fetchCategoryMetas, fetchCategoryMetasReducer),
    on(fetchCategoryMetasSuccess, fetchCategoryMetasSuccessReducer),
    on(fetchCategoryMetasFailure, fetchCategoryMetasFailureReducer),
    on(fetchGridDatasSuccess, fetchGridDatasSuccessReducer),
    on(fetchGridDatasFailure, fetchGridDatasFailureReducer),
    on(loadMoreProducts, loadMoreProductsReducer),
    on(loadMoreProductsSuccess, loadMoreProductsSuccessReducer),
    on(changeDisplayMode, changeDisplayModeReducer),
    on(updateGridFilter, updateGridFilterReducer),
    on(updateMissingFacet, updateMissingFacetReducer),
    on(updateGridFilterPrice, updateGridFilterPriceReducer),
    on(updateGridSort, updateGridSortReducer),
    on(clearFilters, clearFiltersReducer),
    on(clearPriceFilter, clearFilterPriceReducer),
    on(clearSortBy, clearSortByReducer),
    on(openDialog, openDialogReducer),
    on(closeDialog, closeDialogReducer),
    on(setBackToGridProperties, setBackToGridPropertiesReducer),
    on(setIsFromPreviousGrid, setIsFromPreviousGridReducer),
    on(changePreviousPage, changePreviousPageReducer),
    on(reinitializePagesLoaded, reinitializePagesLoadedReducer),
    on(clearMenuLevel, clearMenuLevelReducer),
);
